import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Shield, Users, Activity, Clock, Search, RefreshCw, UserPlus, Ban, Trash2, CheckCircle, Edit, XCircle, Eye, EyeOff, ChevronDown, KeyRound, Mail, LogIn, X, ArrowLeft, Copy } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useToast } from '../../contexts/ToastContext';

interface UserActivity {
  user_email: string;
  last_activity: string;
  last_endpoint: string;
  last_method: string;
  user_group: string;
  status_code: number;
}

interface User {
  username: string;
  email: string;
  status: string;
  enabled: boolean;
  created: string;
  modified: string;
  groups?: string[];  // Optional - backend no longer returns groups (performance optimization)
}

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string, groups: string[], tempPassword?: string) => Promise<void>;
}

function CreateUserModal({ isOpen, onClose, onSubmit }: CreateUserModalProps) {
  const [email, setEmail] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<string[]>(['users']);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Hardcode admin_manager group from Super Admin Portal
      await onSubmit(email, ['admin_manager', 'users'], tempPassword || undefined);
      setEmail('');
      setTempPassword('');
      onClose();
    } catch (err) {
      // Error handling done in parent
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
          <Shield className="w-6 h-6 text-purple-600" />
          <span>Create Admin Manager</span>
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="manager@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Temporary Password (optional)
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                className="w-full px-4 py-2 pr-12 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono"
                placeholder="Leave blank to auto-generate"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate. User will receive email and must change password on first login.</p>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-purple-900">Admin Manager Role:</p>
            <p className="text-xs text-purple-700 mt-1">Can create/manage regular users and admin users (but not other admin_managers or super_admins)</p>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Admin Manager'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface ActivityEvent {
  timestamp: string;
  label: string;
  endpoint: string;
  method: string;
  status_code: number;
}

// Per-row "Actions" dropdown. Rendered through a portal at fixed coordinates so the
// table's horizontal-scroll container (overflow-x-auto) can't clip the menu.
function UserRowActions({ user, busy, onView, onReset, onResend, onSuspendToggle }: {
  user: User;
  busy: boolean;
  onView: () => void;
  onReset: () => void;
  onResend: () => void;
  onSuspendToggle: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setOpen((o) => !o);
  };

  const item = 'w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700';

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        disabled={busy}
        className="px-3 py-1.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
      >
        {busy ? 'Working…' : 'Actions'}
        <ChevronDown className="w-4 h-4" />
      </button>
      {open && createPortal(
        <div
          style={{ position: 'fixed', top: coords.top, right: coords.right, zIndex: 60 }}
          className="w-60 rounded-xl bg-white shadow-xl ring-1 ring-black/5 py-1"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button className={item} onClick={() => { setOpen(false); onView(); }}>
            <Eye className="w-4 h-4 text-gray-500" /> View details
          </button>
          <button className={item} onClick={() => { setOpen(false); onReset(); }}>
            <KeyRound className="w-4 h-4 text-gray-500" /> Reset password
          </button>
          <button className={item} onClick={() => { setOpen(false); onResend(); }}>
            <Mail className="w-4 h-4 text-gray-500" /> Resend login (magic link)
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button
            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${user.enabled ? 'text-yellow-700' : 'text-green-700'}`}
            onClick={() => { setOpen(false); onSuspendToggle(); }}
          >
            <Ban className="w-4 h-4" /> {user.enabled ? 'Suspend user' : 'Unsuspend user'}
          </button>
        </div>,
        document.body
      )}
    </>
  );
}

// Parked for now: the per-user Activity list + archive are hidden until login events are
// captured (needs a Cognito PostAuthentication trigger). Flip to true to re-enable the UI.
const ACTIVITY_ENABLED = false;

export function SuperAdminPortalView() {
  const { showSuccess, showError } = useToast();

  // Restore last active tab from localStorage
  const [activeTab, setActiveTab] = useState<'users' | 'activity'>(() => {
    const saved = localStorage.getItem('superAdminActiveTab');
    return (saved as 'users' | 'activity') || 'users';
  });
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{type: 'suspend' | 'unsuspend', email: string} | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null); // email currently running a row action
  const [detailUser, setDetailUser] = useState<User | null>(null);
  const [detailEvents, setDetailEvents] = useState<ActivityEvent[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveEvents, setArchiveEvents] = useState<ActivityEvent[]>([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  // Magic login link to show the admin for copy/paste (also emailed to the user)
  const [magicLink, setMagicLink] = useState<{ email: string; link: string } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Save active tab to localStorage when it changes
  const handleTabChange = (tab: 'users' | 'activity') => {
    setActiveTab(tab);
    localStorage.setItem('superAdminActiveTab', tab);
  };

  useEffect(() => {
    if (activeTab === 'activity') {
      fetchUserActivity();
    } else {
      fetchUsers();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserActivity = async () => {
    console.log('fetchUserActivity called - Super Admin Portal');
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      console.log('API URL:', apiUrl);
      const response = await fetch(`${apiUrl}/super-admin/user-activity`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch user activity');
      }

      const data = await response.json();
      console.log('Fetched user activity data:', data);
      console.log('Setting activities to:', data.users || []);
      setActivities(data.users || []);
    } catch (err) {
      console.error('Error fetching user activity:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user activity');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (email: string, groups: string[], tempPassword?: string) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          groups,
          temporaryPassword: tempPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create user');
      }

      await fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      showError(err instanceof Error ? err.message : 'Failed to create user');
      throw err;
    }
  };

  const disableUser = async (email: string) => {
    setConfirmAction({type: 'suspend', email});
  };

  const enableUser = async (email: string) => {
    setConfirmAction({type: 'unsuspend', email});
  };

  const confirmUserAction = async () => {
    if (!confirmAction) return;
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) throw new Error('No authentication token found');
      const apiUrl = import.meta.env.VITE_API_URL;
      const endpoint = confirmAction.type === 'suspend' ? 'disable' : 'enable';
      const response = await fetch(`${apiUrl}/admin/users/${confirmAction.email}/${endpoint}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${confirmAction.type} user`);
      }
      showSuccess(`User ${confirmAction.type === 'suspend' ? 'suspended' : 'unsuspended'} successfully`);
      setConfirmAction(null);
      await fetchUsers();
    } catch (err) {
      showError(err instanceof Error ? err.message : `Failed to ${confirmAction.type} user`);
      setConfirmAction(null);
    }
  };

  const deleteUser = async (email: string) => {
    if (!confirm(`Are you sure you want to DELETE ${email}? This action cannot be undone.`)) return;

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users/${email}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete user');
      }

      await fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const promoteToAdmin = async (email: string) => {
    if (!confirm(`Promote ${email} to admin?`)) return;

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users/${email}/groups`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          groups: ['admin', 'users']
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to promote user');
      }

      await fetchUsers();
    } catch (err) {
      console.error('Error promoting user:', err);
      alert(err instanceof Error ? err.message : 'Failed to promote user');
    }
  };

  const demoteFromAdmin = async (email: string) => {
    if (!confirm(`Demote ${email} from admin?`)) return;

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users/${email}/groups`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          groups: ['users']
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to demote user');
      }

      await fetchUsers();
    } catch (err) {
      console.error('Error demoting user:', err);
      alert(err instanceof Error ? err.message : 'Failed to demote user');
    }
  };

  const getIdToken = async () => {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) throw new Error('No authentication token found');
    return idToken;
  };

  const resetPassword = async (email: string) => {
    setRowBusy(email);
    try {
      const idToken = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL;
      const res = await fetch(`${apiUrl}/admin/users/${encodeURIComponent(email)}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || 'Failed to send reset link'); }
      showSuccess(`Password reset link sent to ${email}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to send reset link');
    } finally { setRowBusy(null); }
  };

  const resendLogin = async (email: string) => {
    setRowBusy(email);
    try {
      const idToken = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL;
      const res = await fetch(`${apiUrl}/admin/users/${encodeURIComponent(email)}/resend-login`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || 'Failed to send login link'); }
      const data = await res.json().catch(() => ({}));
      showSuccess(`Login link sent to ${email}`);
      // Also surface the link so the admin can copy/paste it directly to the user
      if (data.link) { setLinkCopied(false); setMagicLink({ email, link: data.link }); }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to send login link');
    } finally { setRowBusy(null); }
  };

  const fetchActivity = async (email: string, windowParam: 'all' | '90d'): Promise<ActivityEvent[]> => {
    const idToken = await getIdToken();
    const apiUrl = import.meta.env.VITE_API_URL;
    const res = await fetch(`${apiUrl}/admin/users/${encodeURIComponent(email)}/activity?window=${windowParam}`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || 'Failed to load activity'); }
    const data = await res.json();
    return (data.events || []) as ActivityEvent[];
  };

  const openDetail = async (user: User) => {
    setDetailUser(user);
    if (!ACTIVITY_ENABLED) return; // activity list parked for now
    setDetailLoading(true);
    setDetailEvents([]);
    try {
      setDetailEvents(await fetchActivity(user.email || user.username, '90d'));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load activity');
    } finally { setDetailLoading(false); }
  };

  const openArchive = async () => {
    if (!detailUser) return;
    setArchiveOpen(true);
    setArchiveLoading(true);
    setArchiveEvents([]);
    try {
      setArchiveEvents(await fetchActivity(detailUser.email || detailUser.username, 'all'));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load archive');
    } finally { setArchiveLoading(false); }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getGroupColor = (group: string) => {
    switch (group) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'users':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getGroupLabel = (group: string) => {
    switch (group) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'users':
        return 'User';
      default:
        return group;
    }
  };

  const getStatusColor = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) return 'text-green-600';
    if (statusCode >= 400 && statusCode < 500) return 'text-yellow-600';
    if (statusCode >= 500) return 'text-red-600';
    return 'text-gray-600';
  };

  const filteredUsers = users.filter(user => {
    // Groups removed from backend for performance - show all users

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matches = user.email?.toLowerCase().includes(query) ||
             user.username?.toLowerCase().includes(query);
      if (!matches) return false;
    }
    // Group filter removed - backend doesn't return groups
    return true;
  });

  const filteredActivities = activities.filter(activity => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matches = activity.user_email.toLowerCase().includes(query) ||
             activity.last_endpoint.toLowerCase().includes(query);
      if (!matches) return false;
    }
    if (groupFilter !== 'all' && activity.user_group !== groupFilter) {
      return false;
    }
    return true;
  });

  const stats = {
    totalUsers: users.length,
    // Group counts removed - backend no longer returns groups for performance
    activeToday: activities.filter(a => {
      const diffMs = new Date().getTime() - new Date(a.last_activity).getTime();
      return diffMs < 86400000;
    }).length
  };

  if (loading && (users.length === 0 && activities.length === 0)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tdb-accent mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-tdb-primary flex items-center space-x-3">
            <Shield className="w-8 h-8 text-purple-600" />
            <span>Super Admin Portal</span>
          </h1>
          <p className="text-gray-600 mt-1">Full system access - manage all users and monitor activity</p>
        </div>
        <div className="flex items-center space-x-3">
          {activeTab === 'users' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all font-semibold"
            >
              <Shield className="w-4 h-4" />
              <span>Create Admin Manager</span>
            </button>
          )}
          <button
            onClick={() => activeTab === 'users' ? fetchUsers() : fetchUserActivity()}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] transition-all font-semibold disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 mb-1">Total Users</div>
              <div className="text-2xl font-bold text-tdb-primary">{stats.totalUsers}</div>
            </div>
            <Users className="w-10 h-10 text-tdb-accent opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 mb-1">Super Admins</div>
              <div className="text-2xl font-bold text-purple-600">{stats.superAdmins}</div>
            </div>
            <Shield className="w-10 h-10 text-purple-600 opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 mb-1">Admins</div>
              <div className="text-2xl font-bold text-blue-600">{stats.admins}</div>
            </div>
            <Shield className="w-10 h-10 text-blue-600 opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 mb-1">Regular Users</div>
              <div className="text-2xl font-bold text-gray-600">{stats.regularUsers}</div>
            </div>
            <Users className="w-10 h-10 text-gray-600 opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 mb-1">Active Today</div>
              <div className="text-2xl font-bold text-green-600">{stats.activeToday}</div>
            </div>
            <Activity className="w-10 h-10 text-green-600 opacity-20" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b-2 border-gray-200">
        <div className="flex space-x-1">
          <button
            onClick={() => handleTabChange('users')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'users'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5" />
              <span>User Management</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('activity')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'activity'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Activity className="w-5 h-5" />
              <span>User Activity</span>
            </div>
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-gray-400" />
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all"
            >
              <option value="all">All User Types</option>
              <option value="super_admin">Super Admins</option>
              <option value="admin">Admins</option>
              <option value="users">Regular Users</option>
            </select>
          </div>

          <div className="relative flex-1 md:max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={activeTab === 'users' ? 'Search users by email or group...' : 'Search by email or endpoint...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all"
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2 text-red-800">
            <Shield className="w-5 h-5" />
            <span className="font-semibold">{error}</span>
          </div>
        </div>
      )}

      {/* User Management Table */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center space-y-2">
                        <Users className="w-12 h-12 text-gray-300" />
                        <p className="text-gray-500">No users found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <button
                          onClick={() => openDetail(user)}
                          className="font-semibold text-gray-900 hover:text-[#1aafff] hover:underline text-left"
                        >
                          {user.email || user.username}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          {user.enabled ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                          <span className={`text-sm font-semibold ${user.enabled ? 'text-green-600' : 'text-red-600'}`}>
                            {user.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700">{formatDate(user.created)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <UserRowActions
                          user={user}
                          busy={rowBusy === (user.email || user.username)}
                          onView={() => openDetail(user)}
                          onReset={() => resetPassword(user.email || user.username)}
                          onResend={() => resendLogin(user.email || user.username)}
                          onSuspendToggle={() =>
                            user.enabled
                              ? disableUser(user.email || user.username)
                              : enableUser(user.email || user.username)
                          }
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Activity Table */}
      {activeTab === 'activity' && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">User Email</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">User Type</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Last Activity</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Last Endpoint</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Method</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredActivities.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center space-y-2">
                        <Activity className="w-12 h-12 text-gray-300" />
                        <p className="text-gray-500">No activity found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredActivities.map((activity, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{activity.user_email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getGroupColor(activity.user_group)}`}>
                          {getGroupLabel(activity.user_group)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700">{formatDate(activity.last_activity)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono text-gray-700">{activity.last_endpoint}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          {activity.last_method}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`font-semibold ${getStatusColor(activity.status_code)}`}>
                          {activity.status_code}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={createUser}
      />

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Confirm {confirmAction.type === 'suspend' ? 'Suspend' : 'Unsuspend'} User
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to {confirmAction.type} <strong>{confirmAction.email}</strong>?
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={confirmUserAction}
                className={`px-4 py-2 text-white rounded-lg font-semibold ${
                  confirmAction.type === 'suspend'
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {confirmAction.type === 'suspend' ? 'Suspend' : 'Unsuspend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Magic login link — copy/paste for the admin (also emailed to the user) */}
      {magicLink && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Magic login link</h3>
              <button onClick={() => setMagicLink(null)} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-700 mb-4">
              A one-click login link was emailed to <strong>{magicLink.email}</strong>. You can also copy it
              below and send it to them directly.
            </p>
            <div className="flex items-center space-x-2 mb-3">
              <input
                readOnly
                value={magicLink.link}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 font-mono truncate"
              />
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(magicLink.link);
                    setLinkCopied(true);
                    showSuccess('Link copied');
                    setTimeout(() => setLinkCopied(false), 2000);
                  } catch {
                    showError('Copy failed — select the text and copy manually');
                  }
                }}
                className="px-4 py-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] font-semibold flex items-center space-x-2 whitespace-nowrap"
              >
                {linkCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{linkCopied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
              ⚠️ This link signs the user in and lets them set a password — treat it like a password.
              It expires in 7 days and can be used only once.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setMagicLink(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Panel (slide-over) */}
      {detailUser && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDetailUser(null)} />
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 break-all">{detailUser.email || detailUser.username}</h2>
                  <div className="mt-1 flex items-center gap-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${detailUser.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {detailUser.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <span className="text-xs text-gray-500">JV Partner</span>
                  </div>
                </div>
                <button onClick={() => setDetailUser(null)} className="p-2 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Actions */}
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  onClick={() => resetPassword(detailUser.email || detailUser.username)}
                  disabled={rowBusy === (detailUser.email || detailUser.username)}
                  className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <KeyRound className="w-4 h-4" /> Reset password
                </button>
                <button
                  onClick={() => resendLogin(detailUser.email || detailUser.username)}
                  disabled={rowBusy === (detailUser.email || detailUser.username)}
                  className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" /> Resend login
                </button>
                <button
                  onClick={() =>
                    detailUser.enabled
                      ? disableUser(detailUser.email || detailUser.username)
                      : enableUser(detailUser.email || detailUser.username)
                  }
                  className={`px-3 py-2 text-sm font-semibold rounded-lg flex items-center justify-center gap-2 ${detailUser.enabled ? 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200' : 'text-green-700 bg-green-100 hover:bg-green-200'}`}
                >
                  <Ban className="w-4 h-4" /> {detailUser.enabled ? 'Suspend user' : 'Unsuspend'}
                </button>
              </div>

              {/* Account info */}
              <div className="mt-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Account</h3>
                <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
                  <dt className="text-gray-500">Email</dt>
                  <dd className="col-span-2 text-gray-900 break-all">{detailUser.email || detailUser.username}</dd>
                  <dt className="text-gray-500">Status</dt>
                  <dd className="col-span-2 text-gray-900">{detailUser.status || (detailUser.enabled ? 'Enabled' : 'Disabled')}</dd>
                  <dt className="text-gray-500">Created</dt>
                  <dd className="col-span-2 text-gray-900">{formatDate(detailUser.created)}</dd>
                </dl>
              </div>

              {/* Activity (last 90 days) — parked until login events are captured */}
              {ACTIVITY_ENABLED && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">Activity · last 90 days</h3>
                  <button onClick={openArchive} className="text-xs font-semibold text-[#1aafff] hover:underline">View archive</button>
                </div>
                <div className="border border-gray-100 rounded-xl max-h-64 overflow-y-auto divide-y divide-gray-50">
                  {detailLoading ? (
                    <div className="p-4 text-center text-sm text-gray-400">Loading activity…</div>
                  ) : detailEvents.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-400">No activity in the last 90 days.</div>
                  ) : (
                    detailEvents.map((ev, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2">
                        <Clock className="w-4 h-4 text-gray-300 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-gray-800 truncate">{ev.label}</div>
                          <div className="text-xs text-gray-400">{formatDate(ev.timestamp)}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Activity Archive (full-screen) */}
      {ACTIVITY_ENABLED && archiveOpen && detailUser && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
          <div className="max-w-3xl mx-auto p-6">
            <button
              onClick={() => setArchiveOpen(false)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-4 h-4" /> Back to user
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Activity archive</h1>
            <p className="text-sm text-gray-500 mt-1 break-all">{detailUser.email || detailUser.username} · complete history</p>
            <div className="mt-6 border border-gray-100 rounded-xl divide-y divide-gray-50">
              {archiveLoading ? (
                <div className="p-6 text-center text-sm text-gray-400">Loading…</div>
              ) : archiveEvents.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-400">No recorded activity.</div>
              ) : (
                archiveEvents.map((ev, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <Clock className="w-4 h-4 text-gray-300 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-800">{ev.label}</div>
                      <div className="text-xs text-gray-400">{formatDate(ev.timestamp)}</div>
                    </div>
                    <span className="text-xs font-mono text-gray-300">{ev.status_code}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
